import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {EBirdMyDataSchema} from "ebird-mydata-reader";
import './big-year.scss';
import YouTube, {YouTubeProps} from "react-youtube";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faForwardStep, faPause, faPlay} from "@fortawesome/free-solid-svg-icons";
import type {SpeciesFamilyLink} from "../App";

export type BigYearProps = {
    observations: EBirdMyDataSchemaWithImage[],
    year: string | number | undefined,
    setObservationImages: (observations: EBirdMyDataSchema[]) => EBirdMyDataSchemaWithImage[],
    familyByTaxonomicOrder: Map<number, SpeciesFamilyLink>
}

export type EBirdMyDataSchemaWithImage = EBirdMyDataSchema & {
    image?: string | undefined;
    speciesGroup?: string | undefined;
}

const VIDEO_ID = 'OlJeS_8jcc0';
const SOUNDTRACK_DURATION_MS = 210000;
const SHOW_START_DELAY_MS = 6000;
const INTRO_REVEAL_GAP_MS = 750;
const FIRST_REVEAL_DELAY_MS = SHOW_START_DELAY_MS + INTRO_REVEAL_GAP_MS;
const SHOW_END_BUFFER_MS = 3000;
const MIN_PHOTO_REVEAL_MS = 240;
const SOUNDTRACK_RETRY_MS = 250;
const SOUNDTRACK_MAX_ATTEMPTS = 12;
const REVIEW_WHEEL_STEP_DELTA = 420;
const REVIEW_WHEEL_MAX_STEPS = 4;
const REVIEW_WHEEL_COOLDOWN_MS = 180;
const INTRO_LINES = 3;
const INTRO_VARIANTS = [
    (yearLabel: string, speciesCount: string) => [
        `In ${yearLabel}, you answered the call of the outdoors.`,
        `You discovered ${speciesCount} species along the way.`,
        "Let's relive every unforgettable moment."
    ],
    (_yearLabel: string, speciesCount: string) => [
        'One year.',
        `${speciesCount} species.`,
        'Countless memories. Ready to revisit them?'
    ],
    (yearLabel: string) => [
        'Some birds were expected.',
        'Others became lifelong memories.',
        `This was ${yearLabel}.`
    ],
    (_yearLabel: string, speciesCount: string) => [
        'You explored.',
        `You discovered ${speciesCount} species.`,
        'Now enjoy the highlights.'
    ],
    (yearLabel: string, speciesCount: string) => [
        'Through forests, fields, and shorelines...',
        `...you discovered ${speciesCount} remarkable species.`,
        `Welcome back to ${yearLabel}.`
    ]
];

export default function BigYear({observations, year, setObservationImages, familyByTaxonomicOrder}: BigYearProps) {
    const rootEl = useRef<HTMLDivElement | null>(null);
    const scrollEl = useRef<HTMLDivElement | null>(null);
    const imagesEl = useRef<HTMLDivElement | null>(null);
    const labelsEl = useRef<HTMLDivElement | null>(null);
    const player = useRef<any>(null);
    const animationFrame = useRef<number>();
    const revealTimer = useRef<number>();
    const introTimer = useRef<number>();
    const soundtrackRetryTimer = useRef<number>();
    const introEndDueAt = useRef(0);
    const remainingIntroDelay = useRef(0);
    const currentOffset = useRef(0);
    const movementStartOffset = useRef(0);
    const movementEndOffset = useRef(0);
    const movementStartTime = useRef(0);
    const movementDuration = useRef(0);
    const photoRevealDuration = useRef(MIN_PHOTO_REVEAL_MS);
    const revealedIndex = useRef(0);
    const nextRevealIndex = useRef(0);
    const nextRevealDueAt = useRef(0);
    const remainingRevealDelay = useRef(0);
    const remainingMovementDelay = useRef(0);
    const revealImageRef = useRef<(imageIndex: number) => void>(() => {});
    const lastReviewWheelAt = useRef(0);
    const currentIndexRef = useRef(0);
    const activeReviewImageIndex = useRef<number | undefined>();
    const reviewScrollFrame = useRef<number>();
    const started = useRef(false);
    const paused = useRef(false);
    const complete = useRef(false);
    const introActive = useRef(false);
    const [images, setImages] = useState<EBirdMyDataSchemaWithImage[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadedCount, setLoadedCount] = useState(0);
    const [totalImages, setTotalImages] = useState(0);
    const [isPlayerReady, setIsPlayerReady] = useState(false);
    const [isIntroActive, setIsIntroActive] = useState(false);
    const [isCounterVisible, setIsCounterVisible] = useState(false);
    const [isReviewMode, setIsReviewMode] = useState(false);
    const [labels, setLabels] = useState<[string, number][]>([]);
    const [imageOffsets, setImageOffsets] = useState<number[]>([]);
    const [introVariantIndex] = useState(() => {
        return Math.floor(Math.random() * INTRO_VARIANTS.length);
    });

    const setVisibleIndex = useCallback((nextIndex: number) => {
        if (currentIndexRef.current === nextIndex) {
            return;
        }

        currentIndexRef.current = nextIndex;
        setCurrentIndex(nextIndex);
    }, []);

    const setReviewActiveImage = useCallback((imageIndex: number) => {
        if (!imagesEl.current || activeReviewImageIndex.current === imageIndex) {
            return;
        }

        if (activeReviewImageIndex.current !== undefined) {
            const previousImage = imagesEl.current.children.item(activeReviewImageIndex.current) as HTMLElement | null;
            if (previousImage) {
                previousImage.classList.remove('state-review-active');
                previousImage.style.zIndex = `${activeReviewImageIndex.current + 1}`;
            }
        }

        const activeImage = imagesEl.current.children.item(imageIndex) as HTMLElement | null;
        if (activeImage) {
            activeImage.classList.add('state-review-active');
            activeImage.style.zIndex = `${imagesEl.current.children.length + 10}`;
            activeReviewImageIndex.current = imageIndex;
        }
    }, []);

    const clearTimers = useCallback(() => {
        if (revealTimer.current) {
            window.clearTimeout(revealTimer.current);
            revealTimer.current = undefined;
        }

        if (animationFrame.current) {
            window.cancelAnimationFrame(animationFrame.current);
            animationFrame.current = undefined;
        }

        if (soundtrackRetryTimer.current) {
            window.clearTimeout(soundtrackRetryTimer.current);
            soundtrackRetryTimer.current = undefined;
        }

        if (introTimer.current) {
            window.clearTimeout(introTimer.current);
            introTimer.current = undefined;
        }

        if (reviewScrollFrame.current) {
            window.cancelAnimationFrame(reviewScrollFrame.current);
            reviewScrollFrame.current = undefined;
        }
    }, []);

    const preloadImage = (observation: EBirdMyDataSchemaWithImage) => new Promise<EBirdMyDataSchemaWithImage | null>((resolve) => {
        if (!observation.image) {
            resolve(null);
            return;
        }

        const img = new Image();
        img.onload = () => resolve(observation);
        img.onerror = () => resolve(null);
        img.src = observation.image;
    });

    useEffect(() => {
        let isCancelled = false;
        clearTimers();
        started.current = false;
        currentOffset.current = 0;
        movementStartOffset.current = 0;
        movementEndOffset.current = 0;
        movementStartTime.current = 0;
        movementDuration.current = 0;
        photoRevealDuration.current = MIN_PHOTO_REVEAL_MS;
        revealedIndex.current = 0;
        nextRevealIndex.current = 0;
        nextRevealDueAt.current = 0;
        remainingRevealDelay.current = 0;
        remainingMovementDelay.current = 0;
        paused.current = false;
        complete.current = false;
        introActive.current = false;
        activeReviewImageIndex.current = undefined;
        introEndDueAt.current = 0;
        remainingIntroDelay.current = 0;
        currentIndexRef.current = 0;
        setCurrentIndex(0);
        setIsPaused(false);
        setImages([]);
        setLabels([]);
        setImageOffsets([]);
        setLoadedCount(0);
        setIsLoading(true);
        setIsIntroActive(false);
        setIsCounterVisible(false);
        setIsReviewMode(false);

        if (imagesEl.current) {
            imagesEl.current.style.transform = 'translate3d(0, 0, 0)';
        }

        if (labelsEl.current) {
            labelsEl.current.style.transform = 'translate3d(0, 0, 0)';
        }

        let tmpObs = setObservationImages(observations);
        tmpObs = tmpObs.sort((a, b) => {
            return a.taxonomicOrder < b.taxonomicOrder ? -1 : 1;
        }).filter(observation => observation.image);

        setTotalImages(tmpObs.length);

        Promise.all(tmpObs.map(observation => (
            preloadImage(observation).then((loadedObservation) => {
                if (!isCancelled) {
                    setLoadedCount((count) => count + 1);
                }

                return loadedObservation;
            })
        ))).then((loadedImages) => {
            if (isCancelled) {
                return;
            }

            const validImages = loadedImages.filter((observation): observation is EBirdMyDataSchemaWithImage => Boolean(observation));
            setImages(validImages);

            let labelMap: {[key: string]: number} = {};
            validImages.forEach((observation, i) => {
                const familyLink = familyByTaxonomicOrder.get(observation.taxonomicOrder);
                const familyName = familyLink?.familyCommonName ?? familyLink?.familyName ?? observation.speciesGroup;

                if (familyName && !labelMap.hasOwnProperty(familyName)) {
                    labelMap[familyName] = i;
                }
            });

            const labelsArray = Object.entries(labelMap);
            labelsArray.sort((a, b) => {
                return a[1] < b[1] ? -1 : 1;
            });
            setLabels(labelsArray);
        });

        return function cleanup() {
            isCancelled = true;
            clearTimers();
        }
    }, [familyByTaxonomicOrder, observations, setObservationImages, clearTimers]);

    useLayoutEffect(() => {
        if (!imagesEl.current || !images.length) {
            return;
        }

        const offsets = Array.from(imagesEl.current.children).map((child) => {
            return (child as HTMLElement).offsetTop;
        });

        const hasDistinctOffsets = offsets.some((offset, i) => i > 0 && offset !== offsets[0]);
        if (hasDistinctOffsets) {
            setImageOffsets(offsets);
            return;
        }

        const firstImage = imagesEl.current.children.item(0) as HTMLElement | null;
        const imageSpacing = firstImage ? firstImage.offsetHeight + parseFloat(getComputedStyle(firstImage).marginBottom) : 0;
        setImageOffsets(offsets.map((_, i) => i * imageSpacing));
    }, [images, isReviewMode]);

    const applyStageOffset = useCallback((offset: number) => {
        currentOffset.current = offset;
        const nextTransform = `translate3d(0, -${offset}px, 0)`;

        if (imagesEl.current) {
            imagesEl.current.style.transform = nextTransform;
        }

        if (labelsEl.current) {
            labelsEl.current.style.transform = nextTransform;
        }
    }, []);

    const getCurrentMovementOffset = useCallback(() => {
        const elapsed = performance.now() - movementStartTime.current;
        const progress = movementDuration.current ? Math.min(Math.max(elapsed / movementDuration.current, 0), 1) : 0;
        return movementStartOffset.current + ((movementEndOffset.current - movementStartOffset.current) * progress);
    }, []);

    const movePhotos = useCallback(() => {
        if (paused.current) {
            return;
        }

        applyStageOffset(getCurrentMovementOffset());

        animationFrame.current = window.requestAnimationFrame(movePhotos);
    }, [applyStageOffset, getCurrentMovementOffset]);

    const startAnimationLoop = useCallback(() => {
        if (animationFrame.current) {
            window.cancelAnimationFrame(animationFrame.current);
        }

        animationFrame.current = window.requestAnimationFrame(movePhotos);
    }, [movePhotos]);

    const getImageOffset = useCallback((index: number) => {
        const imageEl = imagesEl.current?.children.item(index) as HTMLElement | null;
        return imageEl?.offsetTop || imageOffsets[index] || 0;
    }, [imageOffsets]);

    const getImageReviewScrollTop = useCallback((imageIndex: number) => {
        const imageTop = getImageOffset(imageIndex);
        const scrollTopOffset = scrollEl.current ? scrollEl.current.clientHeight * 0.45 : 0;

        return Math.max(0, imageTop - scrollTopOffset);
    }, [getImageOffset]);

    const startMovementToImage = useCallback((imageIndex: number, duration: number) => {
        movementStartOffset.current = currentOffset.current;
        movementEndOffset.current = getImageOffset(imageIndex);
        movementStartTime.current = performance.now();
        movementDuration.current = duration;
    }, [getImageOffset]);

    const getPhotoRevealDuration = useCallback((imageCount: number) => {
        if (!imageCount) {
            return MIN_PHOTO_REVEAL_MS;
        }

        const availableRevealTime = SOUNDTRACK_DURATION_MS - FIRST_REVEAL_DELAY_MS - SHOW_END_BUFFER_MS;
        return Math.max(MIN_PHOTO_REVEAL_MS, availableRevealTime / imageCount);
    }, []);

    const playSoundtrack = useCallback((attempt = 1) => {
        if (paused.current) {
            return;
        }

        const soundtrackPlayer = player.current;

        if (!soundtrackPlayer?.playVideo) {
            return;
        }

        try {
            const iframe = soundtrackPlayer.getIframe?.();
            if (!iframe) {
                throw new Error('YouTube iframe is not ready');
            }

            soundtrackPlayer.playVideo();
        } catch (e) {
            if (attempt < SOUNDTRACK_MAX_ATTEMPTS) {
                soundtrackRetryTimer.current = window.setTimeout(() => {
                    playSoundtrack(attempt + 1);
                }, SOUNDTRACK_RETRY_MS);
                return;
            }

            console.warn('Unable to start Big Year soundtrack', e);
        }
    }, []);

    const pauseSoundtrack = useCallback(() => {
        const soundtrackPlayer = player.current;

        if (!soundtrackPlayer?.pauseVideo) {
            return;
        }

        try {
            soundtrackPlayer.pauseVideo();
        } catch (e) {
            console.warn('Unable to pause Big Year soundtrack', e);
        }
    }, []);

    const enterReviewMode = useCallback(() => {
        clearTimers();
        complete.current = true;
        paused.current = false;
        introActive.current = false;
        remainingIntroDelay.current = 0;
        remainingRevealDelay.current = 0;
        remainingMovementDelay.current = 0;
        setIsPaused(false);
        setIsIntroActive(false);
        setIsCounterVisible(true);
        setIsReviewMode(true);
        setVisibleIndex(images.length);
        pauseSoundtrack();

        window.requestAnimationFrame(() => {
            if (!scrollEl.current || !images.length) {
                return;
            }

            setReviewActiveImage(images.length - 1);
            scrollEl.current.scrollTop = getImageReviewScrollTop(images.length - 1);
        });
    }, [clearTimers, getImageReviewScrollTop, images.length, pauseSoundtrack, setReviewActiveImage, setVisibleIndex]);

    const scheduleReveal = useCallback((imageIndex: number, delay: number) => {
        if (revealTimer.current) {
            window.clearTimeout(revealTimer.current);
        }

        nextRevealIndex.current = imageIndex;
        nextRevealDueAt.current = performance.now() + delay;
        revealTimer.current = window.setTimeout(() => {
            revealImageRef.current(imageIndex);
        }, delay);
    }, []);

    const pauseShow = useCallback(() => {
        if (!started.current || paused.current || complete.current) {
            return;
        }

        const now = performance.now();
        const nextOffset = getCurrentMovementOffset();
        remainingRevealDelay.current = Math.max(0, nextRevealDueAt.current - now);
        remainingMovementDelay.current = Math.max(0, movementDuration.current - (now - movementStartTime.current));
        remainingIntroDelay.current = Math.max(0, introEndDueAt.current - now);
        paused.current = true;
        setIsPaused(true);

        if (revealTimer.current) {
            window.clearTimeout(revealTimer.current);
            revealTimer.current = undefined;
        }

        if (animationFrame.current) {
            window.cancelAnimationFrame(animationFrame.current);
            animationFrame.current = undefined;
        }

        if (soundtrackRetryTimer.current) {
            window.clearTimeout(soundtrackRetryTimer.current);
            soundtrackRetryTimer.current = undefined;
        }

        if (introTimer.current) {
            window.clearTimeout(introTimer.current);
            introTimer.current = undefined;
        }

        applyStageOffset(nextOffset);
        pauseSoundtrack();
    }, [applyStageOffset, getCurrentMovementOffset, pauseSoundtrack]);

    const resumeShow = useCallback(() => {
        if (!started.current || !paused.current || complete.current) {
            return;
        }

        paused.current = false;
        setIsPaused(false);
        movementStartOffset.current = currentOffset.current;
        movementStartTime.current = performance.now();
        movementDuration.current = remainingMovementDelay.current;
        startAnimationLoop();
        scheduleReveal(nextRevealIndex.current, remainingRevealDelay.current);
        if (introActive.current) {
            introEndDueAt.current = performance.now() + remainingIntroDelay.current;
            introTimer.current = window.setTimeout(() => {
                introActive.current = false;
                setIsIntroActive(false);
                setIsCounterVisible(true);
                introTimer.current = undefined;
            }, remainingIntroDelay.current);
        }
        playSoundtrack();
    }, [playSoundtrack, scheduleReveal, startAnimationLoop]);

    const togglePaused = useCallback(() => {
        if (paused.current) {
            resumeShow();
            return;
        }

        pauseShow();
    }, [pauseShow, resumeShow]);

    const revealImage = useCallback((imageIndex: number) => {
        const nextVisibleIndex = imageIndex + 1;
        const nextImageIndex = imageIndex + 1;

        revealedIndex.current = nextVisibleIndex;
        setVisibleIndex(nextVisibleIndex);

        if (nextImageIndex < images.length) {
            startMovementToImage(nextImageIndex, photoRevealDuration.current);
            scheduleReveal(nextImageIndex, photoRevealDuration.current);
        } else {
            enterReviewMode();
        }
    }, [enterReviewMode, images.length, scheduleReveal, setVisibleIndex, startMovementToImage]);

    useEffect(() => {
        revealImageRef.current = revealImage;
    }, [revealImage]);

    const startShow = useCallback(() => {
        if (started.current || !isPlayerReady || !images.length) {
            return;
        }

        started.current = true;
        setIsLoading(false);
        introActive.current = true;
        setIsIntroActive(true);
        setIsCounterVisible(false);

        startAnimationLoop();
        photoRevealDuration.current = getPhotoRevealDuration(images.length);
        scheduleReveal(0, FIRST_REVEAL_DELAY_MS);
        introEndDueAt.current = performance.now() + SHOW_START_DELAY_MS;
        introTimer.current = window.setTimeout(() => {
            introActive.current = false;
            setIsIntroActive(false);
            setIsCounterVisible(true);
            introTimer.current = undefined;
        }, SHOW_START_DELAY_MS);
        playSoundtrack();
    }, [getPhotoRevealDuration, images.length, isPlayerReady, playSoundtrack, scheduleReveal, startAnimationLoop]);

    useEffect(() => {
        startShow();

        return function cleanup() {
            clearTimers();
        }
    }, [clearTimers, startShow]);

    const handlePlayerReady: YouTubeProps['onReady'] = (e) => {
        player.current = e.target;
        setIsPlayerReady(true);
    }

    const handleReviewScroll = useCallback(() => {
        if (!isReviewMode || !scrollEl.current || !images.length) {
            return;
        }

        if (reviewScrollFrame.current) {
            return;
        }

        reviewScrollFrame.current = window.requestAnimationFrame(() => {
            reviewScrollFrame.current = undefined;
            if (!scrollEl.current) {
                return;
            }

            const viewportMarker = scrollEl.current.scrollTop + (scrollEl.current.clientHeight * 0.45);
            const activeImageIndex = imageOffsets.reduce((activeIndex, offset, index) => {
                return offset <= viewportMarker ? index : activeIndex;
            }, 0);

            const nextIndex = Math.min(images.length, activeImageIndex + 1);
            setVisibleIndex(nextIndex);
            setReviewActiveImage(nextIndex - 1);
        });
    }, [imageOffsets, images.length, isReviewMode, setReviewActiveImage, setVisibleIndex]);

    const handleReviewWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
        if (!isReviewMode || !scrollEl.current || !images.length) {
            return;
        }

        event.preventDefault();

        const now = performance.now();
        if (now - lastReviewWheelAt.current < REVIEW_WHEEL_COOLDOWN_MS) {
            return;
        }

        const delta = event.deltaY;
        if (!delta) {
            return;
        }

        const stepCount = Math.min(
            REVIEW_WHEEL_MAX_STEPS,
            Math.max(1, Math.ceil(Math.abs(delta) / REVIEW_WHEEL_STEP_DELTA))
        );
        const direction = delta > 0 ? 1 : -1;
        const nextIndex = Math.min(images.length, Math.max(1, currentIndexRef.current + (direction * stepCount)));

        if (nextIndex === currentIndexRef.current) {
            return;
        }

        lastReviewWheelAt.current = now;
        setVisibleIndex(nextIndex);
        setReviewActiveImage(nextIndex - 1);
        scrollEl.current.scrollTo({
            top: getImageReviewScrollTop(nextIndex - 1),
            behavior: 'smooth'
        });
    }, [getImageReviewScrollTop, images.length, isReviewMode, setReviewActiveImage, setVisibleIndex]);

    const getBigYearLabel = () => {
        if (year === 'life') {
            return 'Your Big Life List'
        }

        return `Your ${year} Big Year`;
    }

    const getLabelPosition = useCallback((i: number) => {
        return {
            top: `${getImageOffset(i)}px`
        }
    }, [getImageOffset]);

    const getLoadingText = () => {
        if (totalImages) {
            return `Loading photos ${loadedCount}/${totalImages}`;
        }

        return 'Loading photos';
    }

    const getIntroYear = () => {
        return year === 'life' ? 'your life list' : year?.toString() ?? 'this year';
    }

    const introLines = INTRO_VARIANTS[introVariantIndex](getIntroYear(), observations.length.toLocaleString());
    const playbackCurrentIndex = isReviewMode ? images.length : currentIndex;
    const bigYearStyle = {
        '--visible-index': currentIndex
    } as React.CSSProperties;
    const imageNodes = useMemo(() => (
        images.map((observation, i) => (
            <div
                className={`big-year__img ${i < playbackCurrentIndex ? 'state-visible' : ''}`}
                key={`${observation.commonName}${i}`}
                style={{zIndex: i + 1, '--image-index': i} as React.CSSProperties}
            >
                <img alt={observation.commonName} src={observation.image} />
                <div className="big-year__img-label">{observation.commonName}</div>
            </div>
        ))
    ), [images, playbackCurrentIndex]);
    const labelNodes = useMemo(() => (
        labels.map(label => (
            <div
                className={`big-year__label ${label[1] < playbackCurrentIndex ? 'state-visible' : ''}`}
                key={label[0]}
                style={{...getLabelPosition(label[1]), '--label-index': label[1]} as React.CSSProperties}
            >
                {label[0]}
            </div>
        ))
    ), [getLabelPosition, labels, playbackCurrentIndex]);

    return (
        <div className={`big-year ${isPaused ? 'state-paused' : ''} ${isReviewMode ? 'state-review' : ''}`} ref={rootEl} style={bigYearStyle}>
            <>
                {isLoading ?
                    <div className="big-year__loading">
                        <div className="big-year__name">{getBigYearLabel()}</div>
                        <div className="big-year__load-anim">{getLoadingText()}&hellip;</div>
                    </div>
                    : null}
                {isIntroActive ?
                    <div className="big-year__intro" aria-live="polite">
                        {introLines.map((line, index) => (
                            <p
                                className="big-year__intro-line"
                                style={{
                                    '--big-year-intro-delay': `${(SHOW_START_DELAY_MS / INTRO_LINES) * index}ms`,
                                    '--big-year-intro-line-duration': `${SHOW_START_DELAY_MS / INTRO_LINES}ms`
                                } as React.CSSProperties}
                                key={line}
                            >
                                {line}
                            </p>
                        ))}
                    </div>
                    : null}
                <div className="big-year__scroll" ref={scrollEl} onScroll={handleReviewScroll} onWheel={handleReviewWheel}>
                    {images.length ?
                        <div className="big-year__images" ref={imagesEl}>
                            {imageNodes}
                        </div>
                        : null
                    }
                    <div className="big-year__labels" ref={labelsEl}>
                        {labelNodes}
                    </div>
                </div>
                <div className={`big-year__count ${isCounterVisible ? 'state-visible' : ''}`}>
                    {currentIndex}
                </div>
                {!isLoading && !isReviewMode ?
                    <button
                        type="button"
                        className="big-year__play-btn"
                        onClick={togglePaused}
                        aria-label={isPaused ? 'Play Big Year' : 'Pause Big Year'}
                    >
                        <FontAwesomeIcon icon={isPaused ? faPlay : faPause} aria-hidden="true" />
                    </button>
                    : null}
                {!isLoading && !isReviewMode ?
                    <button
                        type="button"
                        className="big-year__jump-btn"
                        onClick={enterReviewMode}
                        aria-label="Jump to end"
                    >
                        <FontAwesomeIcon icon={faForwardStep} aria-hidden="true" />
                    </button>
                    : null}
                <YouTube
                    videoId={VIDEO_ID}
                    className="big-year__audio"
                    onReady={handlePlayerReady}
                    />
            </>
        </div>
    )
}
